<template>
  <div>
    <div class="mb-3 flex justify-end">
      <RouterLink
        #default="{ navigate }"
        :to="{ name: 'merchantGroupCreate' }"
      >
        <ActionButton @click="navigate"
          >Create Merchant Group</ActionButton
        >
      </RouterLink>
    </div>

    <BaseCard class="relative">
      <QueryLoading v-if="isLoading" class="min-h-72" />
      <QueryError
        v-else-if="isMerchantGroupsError"
        class="min-h-72"
        :show-retry="true"
        @retry="refetchMerchantGroups"
      >
        Failed to retrieve merchant groups
      </QueryError>
      <template v-else>
        <div class="mb-2 flex w-full justify-center">
          <SearchInput v-model="rawSearch" />
        </div>
        <BaseTable
          :rows="data?.items ?? []"
          :columns="columns"
          :is-loading="isFetching"
        >
          <template #cell-merchants="{ row }">
            <span class="line-clamp-1">
              <template
                v-for="(merchant, i) in row.merchants"
                :key="merchant.id"
              >
                <BaseTableChevronLink
                  :to="{
                    name: 'merchantDetail',
                    params: { tenantId: merchant.id },
                  }"
                  >{{ merchant.name }}</BaseTableChevronLink
                ><span v-if="i < row.merchants.length - 1"
                  >,
                </span>
              </template>
            </span>
          </template>
          <template #cell-merchantGroupName="{ row }">
            <BaseTableChevronLink
              :to="{
                name: 'merchantGroupDetail',
                params: {
                  merchantGroupId: row.id,
                },
              }"
              >{{ row.name }}</BaseTableChevronLink
            >
          </template>
          <template
            #cell-actions="{
              row,
            }: {
              row: ListMerchantGroupsItem
            }"
          >
            <div class="relative flex items-center gap-4">
              <BaseInnerLoading
                v-if="
                  portalLinkIsBeingCreatedForGroupId ===
                  row.id
                "
              />
              <BaseTableButton
                variant="info"
                @click="onOpenGroupPortal(row.id)"
              >
                Open Group Portal
              </BaseTableButton>

              <BaseTableButton
                :to="{
                  name: 'merchantGroupEdit',
                  params: {
                    merchantGroupId: row.id,
                  },
                }"
                variant="info"
              >
                Edit
              </BaseTableButton>
              <BaseTableButton
                variant="danger"
                @click="onDeleteMerchantGroup(row.id)"
              >
                Delete
              </BaseTableButton>
            </div>
          </template>
        </BaseTable>

        <div class="flex justify-end py-2">
          <BasePagination
            v-model="page"
            :page-count="pageCount"
          />
        </div>
      </template>
    </BaseCard>
  </div>

  <BaseConfirmation
    ref="deleteConfirmationRef"
    title="Delete Merchant Group"
    description="Are you sure you want to delete this merchant group?"
    confirm-label="Delete"
    cancel-label="Cancel"
  />

  <RouterView #default="{ Component, route }">
    <BaseDialog
      :is-open="!!Component"
      size="lg"
      @close="router.push({ name: 'merchantGroupIndex' })"
    >
      <component :is="Component" v-bind="route.params" />
    </BaseDialog>
  </RouterView>
</template>

<script setup lang="ts">
import type { TableColumn } from "@/components/BaseTable.vue"
import { requestStaffMerchantGroupPortalLink } from "@/services/merchantGroupAdmins/requestStaffMerchantGroupPortalLink"
import type {
  ListMerchantGroupsItem,
  ListMerchantGroupsFilter,
} from "@/services/merchants/listMerchantGroups"

const rawSearch = ref("")
const debouncedSearch = debouncedRef(rawSearch, 300)

const filters = computed<ListMerchantGroupsFilter>(() => ({
  search: debouncedSearch.value.trim(),
}))

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const router = useRouter()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)

const {
  data,
  isLoading,
  isFetching,
  isError: isMerchantGroupsError,
  refetch: refetchMerchantGroups,
} = useMerchantGroups(
  reactiveComputed(() => ({
    ...filters.value,
    ...pagination.value,
  }))
)

type ColumnId =
  | "merchantGroupName"
  | "merchants"
  | "actions"
const columns: Array<
  TableColumn<ListMerchantGroupsItem, ColumnId>
> = [
  {
    id: "merchantGroupName",
    header: "Name",
    getCellValue: (row) => row.name,
    autoWidth: true,
  },
  {
    id: "merchants",
    header: "Merchants",
    getCellValue: (row) =>
      row.merchants
        .map((merchant) => merchant.name)
        .join(", "),
  },
  {
    id: "actions",
    autoWidth: true,
  },
]

const deleteConfirmationRef = useTemplateRef(
  "deleteConfirmationRef"
)

const onDeleteMerchantGroup = async (
  merchantGroupId: string
) => {
  if (deleteConfirmationRef.value === null) return

  const { isCanceled } =
    await deleteConfirmationRef.value.reveal()
  if (isCanceled) return

  try {
    await deleteMerchantGroup(merchantGroupId)
    toast.success("Merchant group deleted")
  } catch {
    toast.error("Something went wrong")
  }
}

const portalLinkIsBeingCreatedForGroupId = ref<
  string | null
>(null)

const onOpenGroupPortal = async (
  merchantGroupId: string
) => {
  portalLinkIsBeingCreatedForGroupId.value = merchantGroupId

  try {
    const { portalLink } =
      await requestStaffMerchantGroupPortalLink({
        merchantGroupId,
      })

    window.open(portalLink, "_blank", "noopener,noreferrer")
  } catch {
    toast.error("Something went wrong")
  } finally {
    portalLinkIsBeingCreatedForGroupId.value = null
  }
}
</script>
